import type { LeadFormDTO } from 'api-models'
import { ApiClient } from 'services/api-client'

export type FormDraft = Pick<
	LeadFormDTO,
	| 'form_name'
	| 'form_url'
	| 'form_selector'
	| 'verification_mode'
	| 'protect_ads'
	| 'block_bots'
> & {
	flag_crm: Omit<LeadFormDTO['flag_crm'], 'verification'> & {
		verification_token?: string | null
	}
}
export type Counts = { submissions: number; flagged: number; blocked: number }
export type Outcome = {
	key: string
	count: number
	percentage: number
	previous_percentage: number
	delta: number
}
export type Series = Array<{ day: string; counts: Record<string, number> }>
export interface Stats {
	forms: Array<
		Counts & {
			form_id: string
			previous: Counts
			last_submission_at: string | null
		}
	>
	metrics: Counts & { previous: Counts; delta: Counts }
	email_outcomes: Outcome[]
	bot_outcomes: Outcome[]
	email_series: Series
	bot_series: Series
}
export interface Submission {
	id: string
	form_id: string
	form_name: string
	form_url: string
	email: string | null
	created_at: string
	email_outcome: string
	bot_outcome: string
	flagged: boolean
	source: string
	country: string | null
	city: string | null
	client_ip: string | null
	location: string
}
export interface SubmissionQuery {
	form_ids?: string[]
	email_outcomes?: string[]
	sources?: string[]
	locations?: string[]
	days: 7 | 30
	form_id?: string
	email_outcome?: string
	bot_outcome?: string
	source?: string
	query?: string
	location?: string
	sort_by?: string
	sort: 'asc' | 'desc'
	page: number
	limit: number
}
export interface PageCheck {
	resolved_url?: string
	resolved: boolean
	email_field_detected: boolean
	forms: Array<{ name: string | null; email_field_detected: boolean }>
	multiple_forms: boolean
	requires_identification: boolean
}
export interface Setup {
	site_key: string
	secret: string | null
	can_write: boolean
	siteverify_url: string
	help_url?: string
	tutorial_url?: string
}
export const createLeadFormsApi = (accountId: string, assetId: string) => {
	const base = `/accounts/${encodeURIComponent(
		accountId
	)}/assets/${encodeURIComponent(assetId)}/lead-forms`
	const get = async <T>(path: string, params?: object): Promise<T> =>
		(await ApiClient.client.get<{ data: T }>(base + path, { params })).data.data
	const post = async <T>(path: string, body: unknown): Promise<T> =>
		(await ApiClient.client.post<{ data: T }>(base + path, body)).data.data
	return {
		accountId,
		assetId,
		list: () => get<LeadFormDTO[]>(''),
		settings: () => get<Setup>('/settings'),
		stats: (days: 7 | 30, form_id?: string) =>
			get<Stats>('/stats', { days, form_id }),
		submissions: (query: SubmissionQuery) =>
			get<{
				rows: Submission[]
				total: number
				page: number
				limit: number
				facets?: { locations: string[] }
			}>('/submissions', query),
		check: (form_url: string, form_selector: string | null) =>
			post<PageCheck>('/check-url', { form_url, form_selector }),
		verify: (form_url: string, slug: string, form_selector: string | null) =>
			post<{ verified: boolean; checked_at: string }>('/verify-widget', {
				form_url,
				slug,
				form_selector
			}),
		create: (draft: FormDraft) => post<LeadFormDTO>('', draft),
		update: async (id: string, draft: FormDraft) =>
			(
				await ApiClient.client.patch<{ data: LeadFormDTO }>(
					`${base}/${encodeURIComponent(id)}`,
					draft
				)
			).data.data,
		remove: async (id: string) => {
			await ApiClient.client.delete(`${base}/${encodeURIComponent(id)}`)
		},
		export: async (query: SubmissionQuery) =>
			(
				await ApiClient.client.get(`${base}/submissions/export`, {
					params: query,
					responseType: 'blob'
				})
			).data as Blob
	}
}
export type LeadFormsApi = ReturnType<typeof createLeadFormsApi>
export const errorMessage = (error: any) =>
	error?.response?.data?.message ||
	error?.message ||
	'Something went wrong. Please try again.'
export const defaults = (): FormDraft => ({
	form_name: '',
	form_url: '',
	form_selector: null,
	protect_ads: {
		active: true,
		selected_statuses: ['invalid', 'spamTraps'],
		threshold: 2
	},
	block_bots: {
		active: true,
		captcha: true,
		captcha_sensitivity: 'normal',
		block: true,
		block_sensitivity: 'normal'
	},
	flag_crm: { active: false, crm: null, connection_id: null }
})
export const slugify = (name: string) =>
	name
		.toLowerCase()
		.trim()
		.replace(/\s+/g, '-')
		.replace(/[^a-z0-9-]/g, '')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '')
